import type { IdentityService, PlayerProfile, ResultRefresh } from '../network/IdentityService';

type Identity = Pick<IdentityService, 'current' | 'authorization' | 'subscribe' | 'refreshAfterResult'>;
type State = ResultRefresh['state'] | 'idle' | 'syncing';
export interface ResultAccountView {
  action: string;
  bank: string;
  state: State;
  canRetry: boolean;
}

export function bindResultAccount(root: HTMLElement, identity: Identity): ResultAccount {
  const action = root.querySelector<HTMLElement>('[data-result-account-action]');
  const bank = root.querySelector<HTMLElement>('[data-result-account-bank]');
  const retry = root.querySelector<HTMLButtonElement>('[data-result-bank-retry]');
  if (!action || !bank || !retry) throw new Error('Result account controls are missing');
  const feedback = new ResultAccount(identity, view => {
    action.textContent = view.action;
    bank.textContent = view.bank;
    bank.dataset.state = view.state;
    retry.hidden = !view.canRetry;
  });
  retry.addEventListener('click', () => feedback.retry());
  return feedback;
}

/** Displays verified account totals separately from the simulation's run award. */
export class ResultAccount {
  private revision = 0;
  private owner: PlayerProfile | null = null;
  private authorization: string | null = null;
  private state: State = 'idle';
  private receiptId: string | undefined;

  public constructor(private readonly identity: Identity, private readonly render: (view: ResultAccountView) => void) {
    identity.subscribe(profile => {
      if (!this.owner) return;
      if (!this.ownsSession(profile)) {
        this.clear();
        this.publish(null, 'session-changed');
      } else if (this.isRecorded(profile)) {
        // Also accept a later ordinary profile refresh after bounded polling
        // expires. Never add the run reward to a cached account balance.
        this.state = 'recorded';
        this.publish(profile, this.state);
      }
    });
  }

  public show(recordedRun: boolean, receiptId?: string): void {
    this.clear();
    const profile = this.identity.current();
    if (!recordedRun || profile?.guest !== false) {
      this.publish(profile, 'idle');
      return;
    }
    this.owner = profile;
    this.receiptId = receiptId;
    this.authorization = this.identity.authorization();
    void this.sync();
  }

  public retry(): void {
    if (this.state === 'delayed' || this.state === 'unavailable') void this.sync();
  }

  public clear(): void {
    this.revision += 1;
    this.owner = null;
    this.authorization = null;
    this.state = 'idle';
    this.receiptId = undefined;
  }

  private ownsSession(profile: PlayerProfile | null): boolean {
    return Boolean(this.owner && profile?.id === this.owner.id && this.authorization === this.identity.authorization());
  }

  private isRecorded(profile: PlayerProfile | null): boolean {
    if (!profile || !this.owner) return false;
    return this.receiptId ? profile.recordedReceiptId === this.receiptId : profile.matchesPlayed > this.owner.matchesPlayed;
  }

  private async sync(): Promise<void> {
    const revision = ++this.revision;
    this.state = 'syncing';
    this.publish(this.identity.current(), this.state);
    try {
      const result = await this.identity.refreshAfterResult(this.receiptId);
      if (revision !== this.revision || !this.ownsSession(this.identity.current())) return;
      // A subscription may already have observed the recorded result while
      // another bounded refresh finishes. Do not regress it to "delayed".
      if (this.isRecorded(this.identity.current())) {
        this.state = 'recorded';
      } else {
        this.state = result.state;
      }
      this.publish(this.identity.current(), this.state);
    } catch {
      if (revision !== this.revision || !this.ownsSession(this.identity.current())) return;
      this.state = 'unavailable';
      this.publish(this.identity.current(), this.state);
    }
  }

  private publish(profile: PlayerProfile | null, state: State): void {
    const account = profile?.guest === false;
    const labels: Partial<Record<State, string>> = {
      syncing: 'BANK TOTAL SYNCING…',
      delayed: 'BANK CONFIRMATION DELAYED',
      unavailable: 'BANK SYNC UNAVAILABLE',
      'session-changed': 'SESSION CHANGED · LOG IN TO CHECK',
    };
    this.render({
      action: account ? 'VIEW ACCOUNT MANIFEST' : 'SAVE THIS RUN',
      bank: labels[state] ?? (account ? `₵${profile.bankedValue} TOTAL BANK` : 'CREATE A STATION ACCOUNT'),
      state,
      canRetry: account && (state === 'delayed' || state === 'unavailable'),
    });
  }
}
